import { useEffect, useRef } from 'react';
import { MessageBubble } from './MessageBubble';
import { TypingIndicator } from './TypingIndicator';
import type { Message } from '@/hooks/useChat';

interface MessageThreadProps {
  messages: Message[];
  currentUserId: string;
  isTyping?: boolean;
  otherUserName?: string;
}

export function MessageThread({
  messages,
  currentUserId,
  isTyping,
  otherUserName,
}: MessageThreadProps) {
  const bottomRef = useRef<HTMLDivElement>(null);

  // Scroll to bottom when new messages arrive
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages.length, isTyping]);

  if (messages.length === 0 && !isTyping) {
    return (
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="text-center text-brand-muted">
          <p className="text-lg font-medium">No messages yet</p>
          <p className="text-sm mt-1">Say hello to start the conversation!</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-y-auto p-4 space-y-3">
      {messages.map((message) => (
        <MessageBubble
          key={message.id}
          content={message.content}
          isOwnMessage={message.senderId === currentUserId}
          timestamp={message.createdAt}
          status={message.status}
          isAiSuggested={message.isAiSuggested}
        />
      ))}

      {isTyping && <TypingIndicator name={otherUserName} />}

      <div ref={bottomRef} />
    </div>
  );
}
